import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addDays, startOfWeek, type DayKey } from '../../shared/days';
import { WEEKDAY_SHORT } from '../../shared/schedule';
import { formatLongDay } from '../lib/format';
import { ProgressRing } from './ProgressRing';

export interface WeekStripProps {
  selected: DayKey;
  today: DayKey;
  onSelect: (day: DayKey) => void;
  progress: (day: DayKey) => { done: number; total: number };
}

/** Monday–Sunday of the selected day's week, each with its progress; future days are disabled. */
export function WeekStrip({ selected, today, onSelect, progress }: WeekStripProps) {
  const monday = startOfWeek(selected);
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const nextWeek = addDays(selected, 7);
  return (
    <nav className="week glass" aria-label="Woche">
      <button type="button" className="icon-btn week__nav" onClick={() => onSelect(addDays(selected, -7))} aria-label="Vorherige Woche">
        <ChevronLeft size={20} />
      </button>
      <div className="week__days">
        {days.map((day, i) => {
          const { done, total } = progress(day);
          const future = day > today;
          const complete = total > 0 && done >= total;
          return (
            <button
              key={day}
              type="button"
              className={`wday${day === today ? ' wday--today' : ''}${complete ? ' wday--complete' : ''}`}
              aria-pressed={day === selected}
              disabled={future}
              onClick={() => onSelect(day)}
              aria-label={`${formatLongDay(day)}${!future && total > 0 ? `, ${done} von ${total} erledigt` : ''}`}
            >
              <span className="wday__label">{WEEKDAY_SHORT[i]}</span>
              <ProgressRing ratio={future || total === 0 ? 0 : done / total} size={38} stroke={3.5} className="wday__ring">
                {Number(day.slice(8))}
              </ProgressRing>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="icon-btn week__nav"
        onClick={() => onSelect(nextWeek > today ? today : nextWeek)}
        disabled={startOfWeek(selected) === startOfWeek(today)}
        aria-label="Nächste Woche"
      >
        <ChevronRight size={20} />
      </button>
    </nav>
  );
}
